import React from 'react';

import { useIntl } from 'react-intl';

import SitePricingPlanFormModal from 'src/features/pricing/sites/components/form/SitePricingPlanFormModal';
import { pricingPlanMessages } from 'src/features/pricing/messages';
import { siteMessages } from 'src/features/sites/utils/messages';
import { LIST_SITE_PRICING_PLANS_RESOURCE } from 'src/features/pricing/sites/api/useListSitePricingPlans';
import { useUpdateSitePricingPlan } from 'src/features/pricing/sites/api/useUpdateSitePricingPland';
import { SitePricingPlan } from 'src/features/pricing/sites/utils/types';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useParking } from 'src/providers/ParkingProvider';

interface SiteFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedSitePricingPlan: SitePricingPlan;
    children?: React.ReactNode;
}

function UpdateSitePricingPlanFormModal<TData>({
    className,
    semantics,
    children,
    selectedSitePricingPlan,
}: SiteFormModalProps<TData>) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { mutate, isPending } = useUpdateSitePricingPlan({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SITE_PRICING_PLANS_RESOURCE] });
            toast({
                title: formatMessage(siteMessages.updateSiteSuccess),
                description: formatMessage(siteMessages.updateSiteSuccessDescription, {
                    entry: success.translations.find((translation) => translation.language === locale)?.name,
                }),
            });
        },
    });

    return (
        <SitePricingPlanFormModal
            selectedSitePricingPlan={selectedSitePricingPlan}
            className={className}
            mutate={(data, onSuccess, onError) =>
                mutate({ data, planId: selectedSitePricingPlan.id, parkingId }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(pricingPlanMessages.editPricingPlanSubtitle, {
                entry: '',
            })}
        >
            {children}
        </SitePricingPlanFormModal>
    );
}

export default UpdateSitePricingPlanFormModal;
